"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Mountain } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function FoolCardPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-yellow-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-yellow-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-yellow-500">/</span>
            <span className="text-sm">Majör Arkana</span>
            <span className="text-yellow-500">/</span>
            <span className="text-sm">The Fool</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Majör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">The Fool</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-yellow-900/20 to-amber-900/20 rounded-xl p-8 mb-8 border border-yellow-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/rider-waite-fool.svg"
                  alt="The Fool Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-amber-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">0</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">The Fool</h1>
                  <p className="text-xl text-yellow-400">Deli / Başlangıç</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Tarot yolculuğunun başlangıç noktası. Saf potansiyel, masumiyet ve cesur bir adım atma isteği.
                Bilinmeze doğru yapılan yolculuğun sembolü.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm">Yeni başlangıçlar</span>
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm">Spontanlık</span>
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm">Masumiyet</span>
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm">Cesaret</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yeni Başlangıçlar</h3>
                  <p className="text-slate-400 text-sm">Hayatta yeni bir sayfa açma, taze bir başlangıç yapma zamanı.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Spontanlık ve Özgürlük</h3>
                  <p className="text-slate-400 text-sm">Planları bir kenara bırakıp kalp sesini dinleme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Saf Potansiyel</h3>
                  <p className="text-slate-400 text-sm">Sınırsız olasılıklar ve henüz keşfedilmemiş yetenekler.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Cesaret ve İnanç</h3>
                  <p className="text-slate-400 text-sm">Bilinmeze doğru cesurca adım atma cesareti.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Mountain className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Düşüncesizlik</h3>
                  <p className="text-slate-400 text-sm">Sonuçları düşünmeden acele kararlar almak.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Naiflik</h3>
                  <p className="text-slate-400 text-sm">Gerçeklerden kopuk, aşırı iyimser yaklaşım.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Risk Alma Korkusu</h3>
                  <p className="text-slate-400 text-sm">Yeni deneyimlerden kaçınma, güvenli alanda kalma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sorumsuzluk</h3>
                  <p className="text-slate-400 text-sm">Yükümlülüklerden kaçma, ciddi olmama.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                <Heart className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Beyaz Gül</h3>
              <p className="text-slate-400 text-sm">Saflık, masumiyet ve saf aşk. Henüz bozulmamış potansiyeli temsil eder.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-3">
                <Sun className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Güneş</h3>
              <p className="text-slate-400 text-sm">Aydınlanma, pozitif enerji ve yaşam gücü. Yeni günün başlangıcı.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Mountain className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Uçurum</h3>
              <p className="text-slate-400 text-sm">Bilinmeye doğru adım, risk alma ve güven. İnancın gücü.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-brown-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🎒</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Çanta</h3>
              <p className="text-slate-400 text-sm">Bilinçsiz potansiyel, henüz keşfedilmemiş yetenekler ve deneyimler.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-black text-lg">🐕</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Küçük Köpek</h3>
              <p className="text-slate-400 text-sm">Sadakat, koruma ve içsel ses. Sezgilerin uyarısı.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👄</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kırmızı Kıyafet</h3>
              <p className="text-slate-400 text-sm">Tutku, yaşam enerjisi ve fiziksel dünyayla bağlantı.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yeni bir ilişkinin başlangıcı</li>
                <li>• Spontan aşk deneyimleri</li>
                <li>• Önyargısız yaklaşım</li>
                <li>• Kalbin sesini dinlemek</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yeni iş fırsatları</li>
                <li>• Yaratıcı projeler</li>
                <li>• Girişimcilik</li>
                <li>• Risk almayı gerektiren kararlar</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ruhsal yolculuğa başlama</li>
                <li>• Yeni deneyimlere açılma</li>
                <li>• İç çocukla bağlantı</li>
                <li>• Özgürlük arayışı</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Mountain className="h-5 w-5 text-blue-400" />
                Sağlık ve Ruh Hali
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yenilenme ve tazelenme</li>
                <li>• Pozitif enerji</li>
                <li>• Genç kalma isteği</li>
                <li>• Yaşam sevinci</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Majör Arkana Listesi
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/magician"
            className="inline-flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
          >
            The Magician
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}